import React, { useState } from "react";
import { IoCalendarClearOutline, IoLocationOutline, IoPersonOutline, IoTimeOutline } from "react-icons/io5";
import { MdOutlineEventNote } from "react-icons/md";
import EASkeletonLoader from "../shared/EASkeletonLoader";
import EAAlert from "../shared/EAAlert";
import EAButton from "../shared/Buttons/EAButton";
import EAChip from "../shared/Chip/EAChip";
import useSwimmingCourseOffer from "./useSwimmingCourseOffer";
import SwimmingCourseOfferForm from "./SwimmingCourseOfferForm";
import { ExercisePOJO, LessonPass } from "../../models/LessonPass";
import "./style.scss";

const RECRUITMENT_KIND = "SwimmingLessonRecruitment";

const formatTime = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const getSemesterExercises = (list: ExercisePOJO[]): ExercisePOJO[] =>
  list.filter((e) => e.eventKeeperName !== RECRUITMENT_KIND);

const getImageUrl = (list: ExercisePOJO[]): string => {
  const item = list.find((e) => e.eventKeeperName !== RECRUITMENT_KIND && e.eventMessage && e.eventMessage.length > 10);
  return item?.eventMessage ?? "";
};

const isAlreadyPaid = (data: LessonPass): boolean => {
  if (data.billPaymentStatus !== "Paid") return false;
  if (data.billPrice == null || data.grossPrice == null) return false;
  return Math.abs(data.billPrice - data.grossPrice) < 0.01;
};

const SwimmingCourseOffer: React.FC = () => {
  const { data, loading, error } = useSwimmingCourseOffer();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);

  if (loading) {
    return (
      <div className="swimming-course-offer">
        <div className="swimming-course-offer__container">
          <EASkeletonLoader height="32px" />
          <EASkeletonLoader height="220px" />
          <EASkeletonLoader height="180px" />
          <EASkeletonLoader height="60px" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="swimming-course-offer">
        <EAAlert type="error" message={error} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="swimming-course-offer">
        <EAAlert type="error" message="Brak danych oferty." />
      </div>
    );
  }

  const reservationList = data.reservationList ?? [];
  const semesterExercises = getSemesterExercises(reservationList);
  const firstExercise = semesterExercises[0];
  const hasNoTerms = semesterExercises.length < 1;
  const imageUrl = getImageUrl(reservationList);
  const durationMinutes = firstExercise ? Math.round((firstExercise.closeTime - firstExercise.startTime) / 60000) : 0;
  const timeLabel = firstExercise
    ? `${formatTime(firstExercise.startTime)} – ${formatTime(firstExercise.closeTime)}`
    : "";
  const location = firstExercise?.location ?? "";
  const dayOfAWeek = firstExercise?.dayOfAWeek ?? "";
  const description = firstExercise?.description ?? "";
  const entrances = data.entrances ?? data.reservationCount;
  const priceLabel = data.grossPrice != null && data.grossPrice > 0 ? `${data.grossPrice.toFixed(2)} zł` : "—";
  const alreadyPaid = isAlreadyPaid(data);
  const canPay = !alreadyPaid && !hasNoTerms;
  // const visibleOutsideAlerts = (data.outsideAlerts ?? []).filter((a) => a.alert?.trim());
  // const hasOutsideAlerts = visibleOutsideAlerts.length > 0;

  if (showForm && canPay) {
    return (
      <div className="swimming-course-offer">
        <div className="swimming-course-offer__container">
          <h2 className="swimming-course-offer__page-title">Rezerwacja kursu</h2>
          <SwimmingCourseOfferForm onCancel={() => setShowForm(false)} priceLabel={priceLabel} />
        </div>
      </div>
    );
  }

  return (
    <div className="swimming-course-offer">
      <div className="swimming-course-offer__container">
        <h2 className="swimming-course-offer__page-title">Rezerwacja kursu</h2>

        {/* Image header */}
        <div className="swimming-course-offer__image-wrapper">
          {imageUrl ? (
            <div className="swimming-course-offer__image" style={{ backgroundImage: `url(${imageUrl})` }}>
              <div className="swimming-course-offer__image-overlay" />
              <div className="swimming-course-offer__image-content">
                <h4 className="swimming-course-offer__image-title">{data.exerciseGroupName}</h4>
                {durationMinutes > 0 && <EAChip label={`${durationMinutes} min`} />}
              </div>
            </div>
          ) : (
            <div className="swimming-course-offer__image-placeholder">
              <h4 className="swimming-course-offer__image-title">{data.exerciseGroupName}</h4>
            </div>
          )}
        </div>

        {/* Course name + pass chip */}
        <div className="swimming-course-offer__header">
          <h3 className="swimming-course-offer__course-name">{data.exerciseGroupName}</h3>
          <EAChip label={data.name} variant="default" />
        </div>

        {/* Info grid — reuse swimming modal info box styles */}
        <div className="swimming-modal-info-box">
          <div className="swimming-modal-info-box__grid">
            <div className="swimming-modal-info-box__item">
              <IoPersonOutline className="swimming-modal-info-box__item-icon" />
              <div className="swimming-modal-info-box__item-content">
                <span className="swimming-modal-info-box__item-label">Uczestnik</span>
                <span className="swimming-modal-info-box__item-value">{data.accountName}</span>
              </div>
            </div>

            {dayOfAWeek && (
              <div className="swimming-modal-info-box__item">
                <IoCalendarClearOutline className="swimming-modal-info-box__item-icon" />
                <div className="swimming-modal-info-box__item-content">
                  <span className="swimming-modal-info-box__item-label">Dzień zajęć</span>
                  <span className="swimming-modal-info-box__item-value">{dayOfAWeek}</span>
                </div>
              </div>
            )}

            {timeLabel && (
              <div className="swimming-modal-info-box__item">
                <IoTimeOutline className="swimming-modal-info-box__item-icon" />
                <div className="swimming-modal-info-box__item-content">
                  <span className="swimming-modal-info-box__item-label">Godzina</span>
                  <span className="swimming-modal-info-box__item-value">{timeLabel}</span>
                </div>
              </div>
            )}

            {location && (
              <div className="swimming-modal-info-box__item">
                <IoLocationOutline className="swimming-modal-info-box__item-icon" />
                <div className="swimming-modal-info-box__item-content">
                  <span className="swimming-modal-info-box__item-label">Strefa</span>
                  <span className="swimming-modal-info-box__item-value">{location}</span>
                </div>
              </div>
            )}

            {entrances != null && (
              <div className="swimming-modal-info-box__item">
                <MdOutlineEventNote className="swimming-modal-info-box__item-icon" />
                <div className="swimming-modal-info-box__item-content">
                  <span className="swimming-modal-info-box__item-label">Liczba zajęć</span>
                  <span className="swimming-modal-info-box__item-value">{entrances}</span>
                </div>
              </div>
            )}
          </div>

          {description && (
            <div className="swimming-modal-info-box__description">
              <article
                className={`swimming-modal-info-box__description-content${isDescriptionExpanded ? " swimming-modal-info-box__description-content--expanded" : ""}`}
                dangerouslySetInnerHTML={{ __html: description }}
              />
              <button
                className="swimming-modal-info-box__read-more"
                onClick={() => setIsDescriptionExpanded((prev) => !prev)}
              >
                {isDescriptionExpanded ? "Zwiń" : "Pokaż więcej"}
              </button>
            </div>
          )}
        </div>

        {/* Outside alerts — komunikaty widoczne dla klienta */}
        {/* {hasOutsideAlerts && (
          <div className="swimming-course-offer__alerts">
            {visibleOutsideAlerts.map((alert, i) => (
              <EAAlert key={i} type="warning" message={alert.alert} />
            ))}
          </div>
        )} */}

        {/* Price + CTA */}
        <div className="swimming-course-offer__footer">
          {alreadyPaid ? (
            <EAAlert
              type="success"
              message="Zapisy na te zajęcia zostały już opłacone. Ponowna płatność nie jest możliwa."
            />
          ) : hasNoTerms ? (
            <EAAlert type="warning" message="Brak przypisanych terminów zajęć. Płatność nie jest możliwa." />
          ) : (
            <>
              <div className="swimming-course-offer__price-row">
                <span className="swimming-course-offer__price-label">Do zapłaty</span>
                <span className="swimming-course-offer__price-value">{priceLabel}</span>
              </div>
              <EAButton title="Opłać rezerwację" fullWidth onClick={() => setShowForm(true)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwimmingCourseOffer;
